// БЛОК НАХОДКИ ШАГА 4 (B6-2) — адаптер от выхода движка до вью-модели блока.
//
// Борд `Life Map - s4 Layered Map v10.html`, `findingHTML()` (`:457–466`): рельса, надстрочник,
// имена несущих, строка находки, строка доказательств, «поднять на карте».
//
// ⛔ ЗАШИТЫЕ ВЕТКИ БОРДА (`:452–456`) НЕ ПЕРЕНЕСЕНЫ — так сказано в скоупе. Борд сочинял строку
// сам («carries this one alone», «turn at the same points in your life»), и это был макет копи,
// а не копи: судился и закрыт БАНК (`s300.d2`, 15/15), и печатать надо его.
//
// ⛔ ЭТОТ ФАЙЛ НЕ РИСУЕТ И НЕ ЗНАЕТ РАЗМЕТКИ. Скоуп: «чистые функции — таблица входов, без
// браузера»; вставка блока на экран — `B6-3`. Поэтому на выходе значения, а не узлы, и каждое
// отсутствие выражено `null`, а не пустой строкой: пустую строку экран напечатает как пустое
// место и не заметит разницы.
//
// ⛔ В ДВИЖОК ЗДЕСЬ НИЧЕГО НЕ КЛАДЁТСЯ. Читается готовый выход `src/insights/` (`redLine.*`) —
// та самая половина контракта, которая заморожена F1.

import { roman } from '../../roman.js';
import { findingParts } from '../../copy/findingBank.js';

/** Надстрочник блока — байт борда (`:462`). Строка ратифицирована как существующая (`s4.find.eyb`). */
export const FINDING_EYEBROW = 'the shape underneath';

/**
 * Разделитель имён несущих — ВСЕГДА `·` (`s618`, п. 1).
 *
 * ⚠️ У борда здесь ветвление (`c.together ? ' + ' : ' · '`, `:463`), и оно снято НАМЕРЕННО.
 * `+` читается как «сложить», то есть как утверждение о том, ЧТО несущие делают вместе, — а это
 * уже находка, и она печатается строкой ниже. Разделитель обязан только перечислять.
 */
export const CARRIER_SEP = ' · ';

/** Подпись под строкой находки (борд `:465`). */
export const LIFT_LABEL = 'lift them on the map ↑';

/** Слово-приставка строки доказательств (борд `:464`). */
export const EVIDENCE_LABEL = 'evidence · stages';

/**
 * Вью-модель блока находки.
 *
 * @param {object} spec
 * @param {{carrierConfig: string, geometryClass: string, carrier?: Array<number|null>}} spec.view
 *   `redLine` выхода движка.
 * @param {string[]} [spec.names] печатные имена несущих, В ПОРЯДКЕ ВЫВОДА. ⚠️ Приходят снаружи, а
 *   не берутся из словаря: у §IV и у шага 4 они РАЗНЫЕ (`security` против `sense of security`).
 * @param {number[]} [spec.evidenceStages] индексы этапов-доказательств, от нуля.
 * @returns {{eyebrow: string, carriers: string|null, subject: string, predicate: string|null,
 *   separator: string, evidence: string|null, lift: string|null, scatter: boolean}}
 */
export function findingBlock({ view, names = [], evidenceStages = [] } = {}) {
  const parts = findingParts(view);
  // ⭐ «НЕТ НЕСУЩЕЙ» — ВАРИАНТ b (`s493.d2`, `s615.d1`): блок НЕ исчезает. Он говорит ровно то,
  // что знает, — `no single line carries it.` (`s617.d1`) — и молчит обо всём остальном.
  // ⛔ И НЕ ПЕРЕКРАШИВАЕТСЯ (`s617.d2`): отсутствие несущей это ответ, а не сбой, и метить его
  // другим видом значило бы сказать человеку, что он что-то сделал не так.
  // Разброс — по раскладу, не по `predicate === null`: у несущей с одной точкой (Н41) предиката тоже
  // нет, а несущая, её имя и доказательство есть.
  const scatter = view.carrierConfig === 'scatter';

  return {
    eyebrow: FINDING_EYEBROW,
    // Имён нет ни при scatter (несущей нет по построению), ни при пустом списке.
    carriers: scatter || names.length === 0 ? null : names.join(CARRIER_SEP),
    subject: parts.subject,
    predicate: parts.predicate,
    separator: parts.separator,
    // ⭐ ДОКАЗАТЕЛЬСТВ НОЛЬ → СТРОКИ НЕТ ЦЕЛИКОМ, БЕЗ ПРОЧЕРКА (`s493.d2`). У борда здесь
    // `evidence · stages —` (`:464`), и прочерк — худший из трёх исходов: он ОБЕЩАЕТ строку и
    // тут же признаётся, что она пуста. Либо доказательства есть, либо о них не заходит речь.
    evidence: scatter || evidenceStages.length === 0
      ? null
      : `${EVIDENCE_LABEL} ${evidenceStages.map((i) => roman(i)).join(' · ')}`,
    // «Поднять на карте» нечего, когда поднимать нечего.
    lift: scatter ? null : LIFT_LABEL,
    scatter,
  };
}
